import { ReactNode, useEffect, useRef, useState } from "react";
import useVimeo from "../hooks/useVimeo";
import useYouTube from "../hooks/useYoutube";
import { formatDurationFromSeconds } from "../utils/formatDurationFromSeconds";
import { getClassName } from "@pairtreefamily/utils";
import Button from "./button";
import Close from "./icons/close";
import Play from "./icons/play";
import Lightbox from "./lightbox";
import { PlayButton } from "./playButton";

export type VimeoButtonProps = {
  label?: string;
  videoId: string;
  videoHash?: string;
  className?: string;
  showDuration?: boolean;
  displayIconButton?: boolean;
};

export function VimeoButton(props: VimeoButtonProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const player = useVimeo({
    videoId: props.videoId,
    videoHash: props.videoHash,
    videoContainerRef: videoContainerRef,
  });

  useEffect(() => {
    if (player) {
      player.getDuration().then((duration) => setVideoDuration(duration));
    }
  }, [player, setVideoDuration]);

  useEffect(() => {
    if (player && !isOpen) {
      player.pause();
    }
  }, [player, isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          setIsOpen(false);
          break;
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, setIsOpen]);

  return (
    <>
      {!props.displayIconButton ? (
        <VideoButton
          className={props.className}
          onClick={() => setIsOpen(true)}
        >
          <div className="flex w-full flex-row items-center gap-3">
            <Play />
            <div className="flex flex-col items-start">
              <span className="text-body-3-medium">
                {props.label && <strong>{props.label}</strong>}
              </span>
              {props.showDuration && videoDuration > 0 && (
                <span className="text-body-4 -mt-[0.25rem]">
                  {formatDurationFromSeconds(videoDuration)}
                </span>
              )}
            </div>
          </div>
        </VideoButton>
      ) : (
        <PlayButton
          color="white"
          className={props.className}
          onClick={() => setIsOpen(true)}
        />
      )}

      <Lightbox closeLightbox={() => setIsOpen(false)} hidden={!isOpen}>
        <Button
          className="fixed right-2 top-2"
          onClick={() => setIsOpen(false)}
        >
          {/* TODO better close style */}
          <Close className="w-[25px] text-white" unsize />
        </Button>
        <div className="w-full py-16" ref={videoContainerRef} />
      </Lightbox>
    </>
  );
}

export type YouTubeButtonProps = {
  label: string;
  videoId: string;
  className?: string;
};

export function YouTubeButton(props: YouTubeButtonProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const { videoId } = props;

  const player = useYouTube({
    videoId: videoId,
    videoContainerRef: videoContainerRef,
  });

  useEffect(() => {
    if (player) {
      player.getDuration().then((duration) => setVideoDuration(duration));
    }
  }, [player, videoId, setVideoDuration]);

  useEffect(() => {
    if (player && !isOpen) {
      player.pause();
    }
  }, [player, isOpen]);

  useEffect(() => {
    // TODO keydown event doesn't bubble up from the youtube player's iframe;
    // need to implement a proper focus trap so keyboard users can
    // leave gracefully
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "Escape":
          setIsOpen(false);
          break;
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, setIsOpen]);

  return (
    <>
      <VideoButton className={props.className} onClick={() => setIsOpen(true)}>
        <div className="flex w-full flex-row items-center gap-3">
          <Play />
          <div className="flex flex-col items-start">
            <span className="text-body-3-medium">
              <strong>{props.label}</strong>
            </span>
            {videoDuration > 0 && (
              <span className="text-body-4 -mt-[0.25rem]">
                {formatDurationFromSeconds(videoDuration)}
              </span>
            )}
          </div>
        </div>
      </VideoButton>

      <Lightbox closeLightbox={() => setIsOpen(false)} hidden={!isOpen}>
        <Button
          className="fixed right-2 top-2"
          onClick={() => setIsOpen(false)}
        >
          <Close className="w-[25px] text-white" unsize />
        </Button>
        <div className="aspect-video w-full">
          {/* This inner div will be replaced by the embed iframe; 
            the classes will be transfered to that iframe */}
          <div className="h-full w-full" ref={videoContainerRef} />
        </div>
      </Lightbox>
    </>
  );
}

// TODO should be a formal button variant
const VideoButton = (props: {
  children: ReactNode;
  onClick: () => void;
  className?: string;
}) => {
  const buttonClass = getClassName(
    "py-[0.5rem] px-[0.75rem] pr-[2rem]",
    props.className,
  );
  return (
    <Button
      style="primary"
      className={buttonClass}
      color="teal"
      onClick={props.onClick}
    >
      {props.children}
    </Button>
  );
};
